import { datan2 } from '../sim/dmath';
import type { Corner, Straight } from './types';

/** Geometry helpers for a closed, evenly sampled centerline. All deterministic. */

export interface Centerline {
  n: number;
  px: number[];
  py: number[];
  tx: number[];
  ty: number[];
  /** Arc length at each sample. */
  s: number[];
  length: number;
  /** Raw signed curvature, 1/m. */
  kappaRaw: number[];
  /** Smoothed signed curvature, 1/m. */
  kappa: number[];
}

export function buildCenterline(pts: { x: number; y: number }[], smoothRadius = 3): Centerline {
  const n = pts.length;
  const px = pts.map((p) => p.x);
  const py = pts.map((p) => p.y);
  const s = new Array<number>(n);
  s[0] = 0;
  for (let i = 1; i < n; i++) {
    const dx = px[i] - px[i - 1];
    const dy = py[i] - py[i - 1];
    s[i] = s[i - 1] + Math.sqrt(dx * dx + dy * dy);
  }
  const lx = px[0] - px[n - 1];
  const ly = py[0] - py[n - 1];
  const length = s[n - 1] + Math.sqrt(lx * lx + ly * ly);

  const tx = new Array<number>(n);
  const ty = new Array<number>(n);
  const kappaRaw = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const a = (i - 1 + n) % n;
    const b = (i + 1) % n;
    const dx = px[b] - px[a];
    const dy = py[b] - py[a];
    const l = Math.sqrt(dx * dx + dy * dy);
    tx[i] = dx / l;
    ty[i] = dy / l;
    const c1x = px[i] - px[a];
    const c1y = py[i] - py[a];
    const c2x = px[b] - px[i];
    const c2y = py[b] - py[i];
    const l1 = Math.sqrt(c1x * c1x + c1y * c1y);
    const l2 = Math.sqrt(c2x * c2x + c2y * c2y);
    const ang = datan2(c1x * c2y - c1y * c2x, c1x * c2x + c1y * c2y);
    kappaRaw[i] = ang / ((l1 + l2) * 0.5);
  }
  return { n, px, py, tx, ty, s, length, kappaRaw, kappa: smoothCyclic(kappaRaw, smoothRadius) };
}

export function smoothCyclic(arr: number[], radius: number): number[] {
  const n = arr.length;
  const out = new Array<number>(n);
  const w = 2 * radius + 1;
  for (let i = 0; i < n; i++) {
    let sum = 0;
    for (let k = -radius; k <= radius; k++) sum += arr[(i + k + n) % n];
    out[i] = sum / w;
  }
  return out;
}

/** Sliding-window minimum on a cyclic array. */
export function minFilterCyclic(arr: number[], radius: number): number[] {
  const n = arr.length;
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    let m = Infinity;
    for (let k = -radius; k <= radius; k++) {
      const v = arr[(i + k + n) % n];
      if (v < m) m = v;
    }
    out[i] = m;
  }
  return out;
}

export function cyclicDist(a: number, b: number, n: number): number {
  const d = Math.abs(a - b);
  return d < n - d ? d : n - d;
}

/** Walk i..j (inclusive) forward around the loop. */
export function forRange(start: number, end: number, n: number, fn: (i: number) => void): void {
  let i = start;
  for (let guard = 0; guard <= n; guard++) {
    fn(i);
    if (i === end) return;
    i = (i + 1) % n;
  }
}

export function rangeLength(start: number, end: number, n: number): number {
  return ((end - start + n) % n) + 1;
}

export function findCorners(cl: Centerline, threshold: number, mergeGap: number, minAngle: number): Corner[] {
  const n = cl.n;
  const k = cl.kappa;
  const sig = (i: number): number => (k[i] > threshold ? 1 : k[i] < -threshold ? -1 : 0);
  // Start scanning from a sample that is not inside a corner.
  let start = -1;
  for (let i = 0; i < n; i++) {
    if (sig(i) === 0) {
      start = i;
      break;
    }
  }
  if (start < 0) return [];
  const runs: { start: number; end: number; dir: number }[] = [];
  let cur: { start: number; end: number; dir: number } | null = null;
  for (let q = 1; q <= n; q++) {
    const i = (start + q) % n;
    const g = sig(i);
    if (cur && g === cur.dir) {
      cur.end = i;
    } else {
      if (cur) runs.push(cur);
      cur = g !== 0 ? { start: i, end: i, dir: g } : null;
    }
  }
  if (cur) runs.push(cur);

  // Merge same-direction runs separated by a small gap.
  const merged: { start: number; end: number; dir: number }[] = [];
  for (const r of runs) {
    const last = merged[merged.length - 1];
    if (last && last.dir === r.dir && rangeLength(last.end, r.start, n) - 2 <= mergeGap) {
      last.end = r.end;
    } else {
      merged.push({ ...r });
    }
  }

  const corners: Corner[] = [];
  const ds = cl.length / n;
  for (const r of merged) {
    let angle = 0;
    let maxK = 0;
    let apex = r.start;
    forRange(r.start, r.end, n, (i) => {
      angle += k[i] * ds;
      const ak = Math.abs(k[i]);
      if (ak > maxK) {
        maxK = ak;
        apex = i;
      }
    });
    if (Math.abs(angle) < minAngle) continue;
    corners.push({
      start: r.start,
      end: r.end,
      apex,
      dir: r.dir,
      angle,
      minRadius: maxK > 0 ? 1 / maxK : Infinity,
      length: rangeLength(r.start, r.end, n) * ds,
    });
  }
  corners.sort((a, b) => a.start - b.start);
  return corners;
}

export function findStraights(cl: Centerline, maxCurvature: number): Straight[] {
  const n = cl.n;
  const k = cl.kappa;
  const ok = (i: number): boolean => Math.abs(k[i]) < maxCurvature;
  let start = -1;
  for (let i = 0; i < n; i++) {
    if (!ok(i)) {
      start = i;
      break;
    }
  }
  if (start < 0) return [{ start: 0, end: n - 1, length: cl.length }];
  const ds = cl.length / n;
  const out: Straight[] = [];
  let runStart = -1;
  for (let q = 1; q <= n; q++) {
    const i = (start + q) % n;
    if (ok(i)) {
      if (runStart < 0) runStart = i;
    } else if (runStart >= 0) {
      const end = (i - 1 + n) % n;
      out.push({ start: runStart, end, length: rangeLength(runStart, end, n) * ds });
      runStart = -1;
    }
  }
  out.sort((a, b) => b.length - a.length);
  return out;
}

function segmentsIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  const d1 = (dx - cx) * (ay - cy) - (dy - cy) * (ax - cx);
  const d2 = (dx - cx) * (by - cy) - (dy - cy) * (bx - cx);
  const d3 = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const d4 = (bx - ax) * (dy - ay) - (by - ay) * (dx - ax);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export function selfIntersects(px: number[], py: number[]): boolean {
  const n = px.length;
  for (let i = 0; i < n; i++) {
    const i2 = (i + 1) % n;
    for (let j = i + 2; j < n; j++) {
      const j2 = (j + 1) % n;
      if (j2 === i) continue;
      if (segmentsIntersect(px[i], py[i], px[i2], py[i2], px[j], py[j], px[j2], py[j2])) return true;
    }
  }
  return false;
}

/**
 * For every sample, the distance to the nearest part of the circuit that is at
 * least `arcExclusion` metres away along the track, split by side.
 */
export function sideClearance(cl: Centerline, arcExclusion: number, ignore?: (i: number, j: number) => boolean): { left: number[]; right: number[]; min: number } {
  const n = cl.n;
  const ds = cl.length / n;
  const skip = Math.ceil(arcExclusion / ds);
  const left = new Array<number>(n).fill(Infinity);
  const right = new Array<number>(n).fill(Infinity);
  let min = Infinity;
  for (let i = 0; i < n; i++) {
    const nx = -cl.ty[i];
    const ny = cl.tx[i];
    for (let j = 0; j < n; j++) {
      if (cyclicDist(i, j, n) < skip) continue;
      if (ignore && ignore(i, j)) continue;
      const dx = cl.px[j] - cl.px[i];
      const dy = cl.py[j] - cl.py[i];
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < min) min = d;
      if (dx * nx + dy * ny >= 0) {
        if (d < left[i]) left[i] = d;
      } else if (d < right[i]) right[i] = d;
    }
  }
  return { left, right, min };
}
